import { FC, ReactElement, ReactNode, useEffect, useMemo } from 'react'
import type { AppProps } from 'next/app'
import { appWithTranslation } from 'next-i18next'
import { ErrorBoundary } from '@sentry/nextjs'
import { NextPage } from 'next'
import { useRouter } from 'next/router'

import SomethingWentWrong from '~/components/SomethingWentWrong'
import appConfig from '~/config/app'
import { wrapper } from '~/redux/store'
import useSubscription from '~/hooks/useSubscription'
import { useGamification } from '~/gamification/hooks/useGamification'
import { initializeScripts } from '~/utils/tags'
import { GrowthBookProviderWrapper } from '~/utils/growthbook/GrowthBookProvider'
import useUpdateUserTimezone from '~/hooks/useUpdateUserTimezone'
import Modals from '~/components/Modals'
import StillThereModal from '~/components/Modals/StillThereModal'
import ChatAiPanel from '~/aioracle/ChatAiPanel'
import { useLogPageView } from '~/utils/analytics'
import { useImRefCapture } from '~/hooks/useImRefCapture'
import { useImpactIdentify } from '~/hooks/useImpactIdentify'
import AppHead from '~/components/AppHead'
import AnalyticsScript from '~/utils/analytics/scripts'
import useStreak from '~/hooks/useStreak'
import useInitApp from '~/hooks/useInitApp'
import useAuth from '~/hooks/useAuth'
import { useViewTracking } from '~/behavior-tracking/hooks/useViewTracking'
import { CookieConsentScript } from '~/utils/cookieconsent/CookieConsentScript'
import useGetExpectedLanguage from '~/curriculum/hooks/useGetExpectedLanguage'
import { useAppSelector } from '~/redux/hooks'

import '../styles/styles.css'

import shouldRedirectToPlatformLanguage from '~/utils/shouldRedirectToPlatformLang'
import { selectLockRedirection } from '~/curriculum/slice/redirection.slice'
import useSubscriptionWallForFamily from '~/subscription/hooks/useSubscriptionWallForFamily'

initializeScripts()

type NextPageWithLayout = NextPage & {
  getLayout?: (page: ReactElement) => ReactNode
}

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout
}

const HouseOfMathApp: FC<AppPropsWithLayout> = ({ Component, pageProps }) => {
  useAuth()
  useGamification()
  useUpdateUserTimezone()
  useLogPageView()
  useImRefCapture()
  useImpactIdentify()
  useStreak()
  useInitApp()
  const { isIdle, confirmPresence } = useViewTracking()
  useSubscription()
  useSubscriptionWallForFamily()

  const expectedLanguage = useGetExpectedLanguage()
  const router = useRouter()
  const { isReady } = router
  const lockRedirection = useAppSelector(selectLockRedirection)

  const getLayout = useMemo(() => Component.getLayout || ((page: ReactNode) => page), [Component.getLayout])

  useEffect(() => {
    if (!isReady || !expectedLanguage) return

    const redirectToPlatformLanguage = shouldRedirectToPlatformLanguage(router.asPath)
    if (!lockRedirection && router?.locale !== expectedLanguage && redirectToPlatformLanguage) {
      router.push({ pathname: router.pathname, query: router.query }, router.asPath, { locale: expectedLanguage })
    }
  }, [router, isReady, expectedLanguage, lockRedirection])

  return (
    <ErrorBoundary fallback={<SomethingWentWrong />}>
      <AnalyticsScript />
      {/* No cookie-consent stack in the school platform: with no GTM/Meta/Pixel and no
          /v1/process-events, nothing consumes a consent decision. Dropping it also stops the
          CookieInformation vendor script loading and means no banner is ever shown to a student.
          Note initializeScripts() above still runs — it creates the pseudoId that login/SSO/register
          send to hom-api. PostHog is opted in explicitly in instrumentation-client.ts, since this
          component was the only thing that used to do it. */}
      {!appConfig.isSchoolPlatform && <CookieConsentScript />}
      <GrowthBookProviderWrapper>
        <AppHead />
        <Modals />
        {isIdle && <StillThereModal onConfirm={confirmPresence} />}
        <ChatAiPanel />
        {getLayout(<Component {...pageProps} />)}
      </GrowthBookProviderWrapper>
    </ErrorBoundary>
  )
}

export default wrapper.withRedux(appWithTranslation(HouseOfMathApp))
